import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import ScatterPlot, { ScatterPlotDatum, ScatterPlotOptions } from '@/views/components/charts/scatter-plot/ScatterPlot';
import RiskRewardPlotLegend from './RiskRewardPlotLegend';
import SeriesLegend, { LEGEND_TYPES } from '@/views/components/charts/SeriesLegend/SeriesLegend';
import { formatMessage, FormattedMessage } from '@/utils/i18n';

import ComparisonItem from '@/types/comparison-item';
import { getDisclaimerDates } from '@/views/containers/comparison/Charts/utils/risk-reward-chart-helpers';

type Props = {
  chartOptions: ScatterPlotOptions;
  items: ComparisonItem[];
  data: ScatterPlotDatum[];
  dark?: boolean;
  withLegend?: boolean;
  disclaimer?: React.ReactNode;
  emptyState?: React.ReactNode;
  showDisclaimer?: boolean;
  startAndEndDisclaimerDates?: {
    startDate: Date | null;
    endDate: Date | null;
  };
};

const RiskRewardPlot: React.FC<Props> = ({ chartOptions, items, data, dark, withLegend, showDisclaimer = true, emptyState, startAndEndDisclaimerDates }) => {
  const chartHasData = data && data.length > 0;
  const disclaimerDates = getDisclaimerDates(items);

  return (
    <div className={classNames('risk-reward-plot chart', { dark })}>
      {withLegend && chartHasData && <RiskRewardPlotLegend items={items} data={data} showBaseCurrencies={false} />}
      <div className="graph-container">
        {chartHasData && !!items.length && <ScatterPlot dark={dark} data={data} chartOptions={chartOptions} />}
        {!chartHasData && items.length > 0 && (
          <div className={classNames('empty-chart flex-fill', { 'is-light': !dark })}>{emptyState || <FormattedMessage id="Charts.EmptyPeriod" />}</div>
        )}
      </div>
      <SeriesLegend type={LEGEND_TYPES.SCATTER} />
      {showDisclaimer && chartHasData && disclaimerDates.risk && disclaimerDates.performance && (
        <div className="chart-disclaimer">
          <span className="disclaimer">
            {startAndEndDisclaimerDates && startAndEndDisclaimerDates.startDate && startAndEndDisclaimerDates.endDate ? (
              <FormattedMessage
                id="RiskRewardPlot.DatesDisclaimerWithStartAndEnd"
                values={{
                  riskDate: moment.utc(disclaimerDates.risk).format(formatMessage('Common.DateFormats.Short')),
                  startDate: moment.utc(startAndEndDisclaimerDates.startDate).format(formatMessage('Common.DateFormats.Short')),
                  endDate: moment.utc(startAndEndDisclaimerDates.endDate).format(formatMessage('Common.DateFormats.Short')),
                }}
              />
            ) : (
              <FormattedMessage
                id="RiskRewardPlot.DatesDisclaimer"
                values={{
                  riskDate: moment.utc(disclaimerDates.risk).format(formatMessage('Common.DateFormats.Short')),
                  performanceDate: moment.utc(disclaimerDates.performance).format(formatMessage('Common.DateFormats.Short')),
                }}
              />
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default RiskRewardPlot;
